import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  HostListener,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ChatBotService, ChatMessage } from './chatbot.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ScrollLockService } from '@core/services/scroll-lock.service';
import { RAGContext } from './chatbot-rag.models';
import { NinjaCreditsService, CreditSummary } from '@core/services/ninja-credits.service';
import { NINJA_CREDIT_COSTS, NinjaCreditAction } from '@core/services/tier-access.service';

/**
 * Represents the status information for an active streaming operation.
 */
interface StreamStatus {
  /** The type of streaming event (e.g., 'start', 'incomplete', 'done'). */
  type?: string;
  /** A status or informational message associated with the streaming event. */
  message?: string;
}

@Component({
  selector: 'app-floating-chatbot',
  templateUrl: './floating-chatbot.component.html',
  styleUrls: ['./floating-chatbot.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FloatingChatbotComponent implements OnInit, OnDestroy {
  private chatBotService = inject(ChatBotService);
  private cdr = inject(ChangeDetectorRef);
  private sanitizer = inject(DomSanitizer);
  private router = inject(Router);
  private scrollLock = inject(ScrollLockService);
  public ninjaCreditsService = inject(NinjaCreditsService);

  /** Current credit ledger summary — drives balance chip and low-credit warning */
  creditSummary: CreditSummary | null = null;

  /** Credit cost for a standard AI query (15 credits) */
  readonly aiQueryCost = NINJA_CREDIT_COSTS[NinjaCreditAction.AI_QUERY_STANDARD];

  @ViewChild('chatMessages', { static: false })
  chatMessagesContainer!: ElementRef;
  @ViewChild('queryInput', { static: false }) queryInput!: ElementRef;

  messages: ChatMessage[] = [];
  currentQuery = '';
  isStreaming = false;
  isChatOpen = false;
  isMinimized = false;
  hasNewMessages = false;
  isExpanded = false;
  showBackdrop = false;

  /**
   * Retrieval-augmented generation (RAG) context for the current or last AI response.
   *
   * Populated with supporting documents, scan data, vulnerabilities, and metadata returned
   * alongside a streamed answer. This allows the UI to surface "why" context to the user,
   * showing what data informed the AI's response. Reset when a new query starts, the
   * conversation is cleared, or when the backend indicates no additional RAG context is available.
   */
  ragContext: Partial<RAGContext> | null = null;

  /**
   * Status information for the active streaming operation.
   *
   * Tracks lightweight state such as informational, progress, or error messages emitted
   * while the AI response is being streamed. Updated by streaming subscriptions when new
   * status events arrive, and typically cleared when a new query is submitted or the
   * current stream completes.
   */
  streamStatus: StreamStatus = {};

  private subscriptions = new Subscription();

  // Example queries for quick access
  exampleQueries = [
    'Give me a summary of the last 3 scans and top vulnerabilities',
    'What are the highest CVSS score vulnerabilities from recent scans?',
    'How do I start a new scan?',
    'Where can I find my security reports?',
  ];

  private supportTicketListener: any;

  ngOnInit(): void {
    // Listen for support ticket event from error button
    this.supportTicketListener = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.url) {
        // Use Angular router to navigate (strip domain if present)
        let url = customEvent.detail.url;
        if (url.startsWith(window.location.origin)) {
          url = url.substring(window.location.origin.length);
        }
        this.router.navigateByUrl(url);
      }
    };
    window.addEventListener(
      'sn-create-support-ticket',
      this.supportTicketListener,
    );
    // Subscribe to messages
    this.subscriptions.add(
      this.chatBotService.messages$.subscribe((messages) => {
        const oldCount = this.messages.length;
        this.messages = messages;

        // Check for new messages when chat is closed
        if (!this.isChatOpen && messages.length > oldCount && oldCount > 0) {
          this.hasNewMessages = true;
        }

        this.scrollToBottom();
        this.cdr.detectChanges();
      }),
    );

    // Subscribe to streaming status
    this.subscriptions.add(
      this.chatBotService.isStreaming$.subscribe((streaming) => {
        this.isStreaming = streaming;
        this.cdr.detectChanges();
      }),
    );

    // Subscribe to rag context snapshots (truncated) and show lightweight summary
    this.subscriptions.add(
      this.chatBotService.ragContext$.subscribe((ctx) => {
        this.ragContext = ctx;
        // Auto-scroll so users see context info appear
        this.scrollToBottom();
        this.cdr.detectChanges();
      }),
    );

    // Subscribe to streaming event feed for minimal status text (optional UX)
    // TODO: Wire streamStatus into the chatbot UI to surface lightweight streaming status feedback.
    this.subscriptions.add(
      this.chatBotService.streaming$.subscribe((evt) => {
        this.streamStatus = { type: evt?.type, message: evt?.message };
      }),
    );

    // Subscribe to credit balance for the chatbot balance chip
    this.subscriptions.add(
      this.ninjaCreditsService.creditSummary$.subscribe((summary) => {
        this.creditSummary = summary;
        this.cdr.detectChanges();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.chatBotService.cleanup();
    if (this.supportTicketListener) {
      window.removeEventListener(
        'sn-create-support-ticket',
        this.supportTicketListener,
      );
    }
    if (this.isExpanded) {
      this.scrollLock.unlock();
    }
  }

  /**
   * Toggle chat window open/closed
   */
  toggleChat(): void {
    this.isChatOpen = !this.isChatOpen;
    this.isMinimized = false;

    if (this.isChatOpen) {
      this.hasNewMessages = false;
      setTimeout(() => {
        this.scrollToBottom();
        this.focusInput();
      }, 100);
    } else {
      this.isExpanded = false; // reset expansion when closing
      // Reset session state when closing (stops streaming, clears context)
      this.chatBotService.resetSession();
    }
  }

  /**
   * Minimize chat window
   */
  minimizeChat(): void {
    // If currently expanded, collapse first then minimize
    if (this.isExpanded) {
      this.isExpanded = false;
      this.showBackdrop = false;
      // allow animation frame to apply class removal before minimizing
      requestAnimationFrame(() => (this.isMinimized = true));
    } else {
      this.isMinimized = true;
    }
  }

  /**
   * Maximize chat window
   */
  maximizeChat(): void {
    this.isMinimized = false;
    setTimeout(() => {
      this.scrollToBottom();
      this.focusInput();
    }, 100);
  }

  /**
   * Toggle expanded (large centered) view
   */
  toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
    this.showBackdrop = this.isExpanded;
    if (!this.isExpanded) {
      // ensure focus returns and scroll updates when restoring size
      setTimeout(() => this.focusInput(), 120);
    }
    if (this.isExpanded) {
      this.scrollLock.lock();
    } else {
      this.scrollLock.unlock();
    }
    setTimeout(() => this.scrollToBottom(), 150);
  }

  /**
   * Send message
   */
  sendMessage(): void {
    if (!this.currentQuery.trim() || this.isStreaming) return;

    const query = this.currentQuery.trim();
    this.currentQuery = '';

    // Use streaming for better UX
    this.chatBotService.sendStreamingQuery(query);

    // Auto-scroll to show the response
    setTimeout(() => this.scrollToBottom(), 100);
  }

  /**
   * Send example query
   */
  sendExampleQuery(query: string): void {
    if (this.isStreaming) return;

    this.currentQuery = query;
    this.sendMessage();
  }

  /**
   * Clear chat history
   */
  clearChat(): void {
    this.chatBotService.clearMessages();
    this.hasNewMessages = false;
  }

  /**
   * Stop ongoing streaming response
   */
  stopStreaming(): void {
    this.chatBotService.stopStreaming();
  }

  /**
   * Restart conversation (clear and reset)
   */
  restartConversation(): void {
    this.clearChat();
    this.chatBotService.resetSession();
  }

  /**
   * Handle enter key in textarea
   */
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  /**
   * Get message CSS classes
   */
  getMessageClasses(message: ChatMessage): string {
    const baseClass = 'message';
    let typeClass = `message-${message.type}`;

    // Check if it's a system message with error content
    if (message.type === 'system' && this.isErrorMessage(message.content)) {
      typeClass = 'message-error';
    }

    const streamingClass = message.isStreaming ? 'message-streaming' : '';

    return [baseClass, typeClass, streamingClass].filter((c) => c).join(' ');
  }

  /**
   * Check if message content indicates an error
   */
  private isErrorMessage(content: string): boolean {
    const errorKeywords = [
      'error',
      'failed',
      'HTTP',
      '404',
      '500',
      'connection error',
      'not found',
    ];
    const lowerContent = content.toLowerCase();
    return errorKeywords.some((keyword) =>
      lowerContent.includes(keyword.toLowerCase()),
    );
  }

  /**
   * Format message content (decide whether to allow embedded HTML)
   */

  getMessageHtml(message: ChatMessage): SafeHtml {
    // Allow raw HTML for system messages OR bot messages detected as error (contains support link)
    if (
      message.type === 'system' ||
      (message.type === 'bot' &&
        this.isErrorMessage(message.content) &&
        message.content.includes('<a'))
    ) {
      return this.sanitizer.bypassSecurityTrustHtml(message.content);
    }
    return this.sanitizer.bypassSecurityTrustHtml(
      this.formatMessageContent(message.content),
    );
  }

  /**
   * Format message content for display (markdown-like, escapes HTML for safety)
   */
  formatMessageContent(content: string): string {
    // Escape HTML first
    const escaped = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    // Basic markdown-like formatting
    return escaped
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/\n/g, '<br>');
  }

  /**
   * Check if message has context information
   */
  hasContextInfo(message: ChatMessage): boolean {
    return (
      message.type === 'bot' && !message.isStreaming && !!message.contextItems
    );
  }

  /**
   * Get context info text
   */
  getContextInfo(message: ChatMessage): string {
    if (!message.contextItems) return '';
    return `Analysis based on ${message.contextItems} data points from your security scans`;
  }

  /**
   * Get structured RAG context summary data for safe template binding.
   *
   * Returns an object with individual data points that can be rendered
   * safely in the Angular template without using innerHTML.
   */
  getRagSummary(): { scans: number; vulns: number; assets?: number; genAt?: string } | null {
    if (!this.ragContext) return null;
    try {
      const scans = this.ragContext.scans?.length ?? 0;
      const vulns = this.ragContext.vulnerabilities?.length ?? 0;
      // Use bracket notation to access index signature property
      const assetCount = this.ragContext.extra?.['asset_count'];
      const assets = typeof assetCount === 'number' ? assetCount : undefined;
      const genAt = this.ragContext.generated_at;
      return {
        scans,
        vulns,
        assets,
        genAt: genAt ? new Date(genAt).toLocaleString() : undefined,
      };
    } catch {
      return null;
    }
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.chatMessagesContainer?.nativeElement) {
        const element = this.chatMessagesContainer.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    }, 50);
  }

  private focusInput(): void {
    if (this.queryInput?.nativeElement) {
      this.queryInput.nativeElement.focus();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(ev: Event): void {
    const keyboardEvent = ev as KeyboardEvent;
    if (this.isChatOpen && this.isExpanded) {
      keyboardEvent.preventDefault();
      this.toggleExpand();
    }
  }
}
